import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Mail,
  ChevronRight,
  ShieldCheck,
  Check,
  X,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import { track } from '../lib/analytics';

/**
 * PressKit — v4.1 Phase 9.
 *
 * Institutional / media / investor / partner press page for HussarAi's
 * European immigration record integrity positioning.
 *
 * Route: /press-kit (public, unprotected, zero backend).
 *
 * Legacy v3 CTAs to sales-lead and industry lead-magnet pages are
 * intentionally removed — Press Kit is a trust surface and must not
 * bleed sales-lead or industry-lead-magnet positioning.
 */
const CYAN = '#7DD3FC';
const CYAN_STRONG = '#38BDF8';

const PressKit = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    track('page_view', { page: 'press_kit' });
    document.title = t('pressKit.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('pressKit.metaDescription'));

    // v4.1 Phase 9 · Self-referential canonical + hreflang (Phase 8 parity).
    const setLink = (rel, hreflang, href) => {
      const sel = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    setLink('canonical', null, 'https://hussarai.org/press-kit');
    setLink('alternate', 'pl-PL', 'https://hussarai.org/press-kit');
    setLink('alternate', 'en-GB', 'https://hussarai.org/press-kit?lang=en');
    setLink('alternate', 'x-default', 'https://hussarai.org/press-kit');

    return () => {
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.setAttribute('href', 'https://hussarai.org/');
    };
  }, [t]);

  // v4.1 Phase 9 · WebPage + BreadcrumbList JSON-LD.
  // Approved schema.org types only: WebPage, BreadcrumbList, ListItem.
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://hussarai.org/press-kit#page',
    url: 'https://hussarai.org/press-kit',
    name: t('pressKit.metaTitle'),
    description: t('pressKit.metaDescription'),
    inLanguage: isEN ? 'en-GB' : 'pl-PL',
    isPartOf: { '@id': 'https://hussarai.org/#website' },
    about: { '@id': 'https://hussarai.org/#org' },
    significantLink: [
      'https://hussarai.org/review-akt-imigracyjnych',
      'https://hussarai.org/bezpieczenstwo-i-zgodnosc',
      'https://hussarai.org/dla-instytucji',
    ],
  };
  const jsonLdBreadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'HussarAi', item: 'https://hussarai.org/' },
      { '@type': 'ListItem', position: 2, name: t('pressKit.h1'),
        item: 'https://hussarai.org/press-kit' },
    ],
  };

  const longParagraphs = t('pressKit.longBoilerplate.paragraphs', { returnObjects: true }) || [];
  const mayItems = t('pressKit.mayVsMustNot.may', { returnObjects: true }) || [];
  const mustNotItems = t('pressKit.mayVsMustNot.mustNot', { returnObjects: true }) || [];
  const factItems = t('pressKit.facts.items', { returnObjects: true }) || [];
  const linkItems = t('pressKit.links.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="press-kit-page">
      {/* v4.1 Phase 9 · Structured data (safe-by-design local constants). */}
      <script
        type="application/ld+json"
        data-testid="press-kit-jsonld-webpage"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
      />
      <script
        type="application/ld+json"
        data-testid="press-kit-jsonld-breadcrumbs"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumbs) }}
      />

      <HussarTopNav />

      <main className="hussar-agenci-main">
        {/* Hero */}
        <section className="hussar-agenci-hero" data-testid="press-kit-hero">
          <div className="hussar-agenci-eyebrow" data-testid="press-kit-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" />
            {t('pressKit.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="press-kit-h1">
            {t('pressKit.h1')}
          </h1>
          <p className="hussar-agenci-subhead" data-testid="press-kit-subhead">
            {t('pressKit.subhead')}
          </p>
        </section>

        {/* Doctrine strip */}
        <section
          className="hussar-pol-op-section"
          data-testid="press-kit-doctrine"
          style={{
            background: 'rgba(30,41,59,0.55)',
            borderLeft: `3px solid ${CYAN_STRONG}`,
            borderRadius: 14,
            padding: '24px 26px',
          }}
        >
          <p style={{ color: 'rgba(226,232,240,0.92)', marginBottom: 8, fontSize: 16 }}
             data-testid="press-kit-doctrine-positioning">
            {t('pressKit.doctrine.positioning')}
          </p>
          <p style={{ color: CYAN_STRONG, fontWeight: 700, marginBottom: 8, fontSize: 16 }}
             data-testid="press-kit-doctrine-line1">
            {t('pressKit.doctrine.line1')}
          </p>
          <p style={{ color: 'rgba(226,232,240,0.88)', marginBottom: 8, fontSize: 15 }}
             data-testid="press-kit-doctrine-line2">
            {t('pressKit.doctrine.line2')}
          </p>
          <p style={{ color: 'rgba(226,232,240,0.85)', fontStyle: 'italic', fontSize: 15 }}
             data-testid="press-kit-doctrine-line3">
            {t('pressKit.doctrine.line3')}
          </p>
        </section>

        {/* One-liner */}
        <section data-testid="press-kit-one-liner" style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.oneLiner.title')}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(226,232,240,0.92)' }}
             data-testid="press-kit-one-liner-body">
            {t('pressKit.oneLiner.body')}
          </p>
        </section>

        {/* Short boilerplate */}
        <section data-testid="press-kit-short-boilerplate" style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.shortBoilerplate.title')}
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.88)' }}
             data-testid="press-kit-short-boilerplate-body">
            {t('pressKit.shortBoilerplate.body')}
          </p>
        </section>

        {/* Long boilerplate */}
        <section data-testid="press-kit-long-boilerplate" style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.longBoilerplate.title')}
          </h2>
          {longParagraphs.map((p, idx) => (
            <p key={idx}
               data-testid={`press-kit-long-paragraph-${idx + 1}`}
               style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
              {p}
            </p>
          ))}
        </section>

        {/* May do / Must not do */}
        <section data-testid="press-kit-may-vs-mustnot" style={{ marginTop: 44 }}>
          <div
            className="hussar-may-vs-mustnot-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 18,
            }}
          >
            <Card
              data-testid="press-kit-may-card"
              style={{
                background: 'rgba(15,23,42,0.6)',
                borderLeft: `3px solid ${CYAN_STRONG}`,
                borderRadius: 14,
                padding: '20px 22px',
              }}
            >
              <h3 style={{ color: CYAN, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>
                {t('pressKit.mayVsMustNot.titleMay')}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {mayItems.map((item, idx) => (
                  <li key={idx}
                      data-testid={`press-kit-may-item-${idx + 1}`}
                      style={{ display: 'flex', gap: 8, marginBottom: 8, color: 'rgba(226,232,240,0.88)', fontSize: 15 }}>
                    <Check className="w-4 h-4 mt-1" style={{ color: CYAN_STRONG, flexShrink: 0 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card
              data-testid="press-kit-mustnot-card"
              style={{
                background: 'rgba(15,23,42,0.6)',
                borderLeft: '3px solid rgba(248,113,113,0.6)',
                borderRadius: 14,
                padding: '20px 22px',
              }}
            >
              <h3 style={{ color: 'rgba(252,165,165,0.95)', fontWeight: 700, fontSize: 18, marginBottom: 12 }}>
                {t('pressKit.mayVsMustNot.titleMustNot')}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {mustNotItems.map((item, idx) => (
                  <li key={idx}
                      data-testid={`press-kit-mustnot-item-${idx + 1}`}
                      style={{ display: 'flex', gap: 8, marginBottom: 8, color: 'rgba(226,232,240,0.85)', fontSize: 15 }}>
                    <X className="w-4 h-4 mt-1" style={{ color: 'rgba(248,113,113,0.85)', flexShrink: 0 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        {/* Facts */}
        <section data-testid="press-kit-facts" style={{ marginTop: 44 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.facts.title')}
          </h2>
          <div
            style={{
              background: 'rgba(15,23,42,0.55)',
              borderRadius: 14,
              padding: '18px 22px',
            }}
          >
            {factItems.map((fact, idx) => (
              <div key={idx}
                   data-testid={`press-kit-fact-${idx + 1}`}
                   style={{
                     display: 'grid',
                     gridTemplateColumns: 'minmax(140px, 220px) 1fr',
                     gap: 16,
                     padding: '10px 0',
                     borderBottom: idx < factItems.length - 1 ? '1px solid rgba(148,163,184,0.15)' : 'none',
                   }}>
                <div style={{ color: 'rgba(148,163,184,0.85)', fontSize: 14, fontWeight: 500 }}>{fact.k}</div>
                <div style={{ color: 'rgba(226,232,240,0.95)', fontSize: 15 }}>{fact.v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Founder — SEO/entity clarity. Bilingual bio; links to canonical
            founder profile page. Kept restrained, no personal details.
            Same canonical portrait as /konrad-kwiatkowski — no duplicate file. */}
        <section data-testid="press-kit-founder-section" style={{ marginTop: 44 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.founder.title')}
          </h2>
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0, width: 120 }}>
              <picture>
                <source
                  srcSet="/images/konrad-kwiatkowski-founder-hussarai.webp"
                  type="image/webp"
                />
                <img
                  src="/images/konrad-kwiatkowski-founder-hussarai.jpg"
                  alt="Konrad Kwiatkowski, founder of HussarAi"
                  title="Konrad Kwiatkowski — Founder of HussarAi"
                  width={1152}
                  height={1536}
                  loading="lazy"
                  data-testid="press-kit-founder-portrait"
                  style={{
                    display: 'block', width: 120, height: 160,
                    objectFit: 'cover', borderRadius: 8,
                    border: '1px solid rgba(148,163,184,0.18)',
                  }}
                />
              </picture>
            </div>
            <p
              data-testid="press-kit-founder-bio"
              style={{
                fontSize: 15, lineHeight: 1.7, color: '#CBD5F5',
                maxWidth: 720, margin: 0, flex: '1 1 320px',
              }}
            >
              <Link
                to="/konrad-kwiatkowski"
                data-testid="press-kit-founder-link"
                style={{ color: CYAN, textDecoration: 'underline' }}
              >
                Konrad Kwiatkowski
              </Link>{' '}
              {t('pressKit.founder.bio')}
            </p>
          </div>
          {/* Entity-disambiguation note (2026-02-19) — small quiet paragraph
              below the founder bio. This is the ONLY approved location where
              the confusable near-spellings may appear on public SEO surfaces. */}
          <p
            data-testid="press-kit-founder-disambiguation"
            style={{
              marginTop: 14, fontSize: 12, lineHeight: 1.6,
              color: 'rgba(148,163,184,0.75)', maxWidth: 720,
            }}
          >
            {t('pressKit.founder.disambiguation')}
          </p>
        </section>

        {/* Contact */}
        <section data-testid="press-kit-contact-section" style={{ marginTop: 44 }}>
          <Card className="hussar-press-contact-card" data-testid="press-kit-contact">
            <div className="hussar-press-contact-icon"><Mail className="w-6 h-6" /></div>
            <div>
              <h3 className="hussar-press-contact-title" data-testid="press-kit-contact-title">
                {t('pressKit.contact.title')}
              </h3>
              <p className="hussar-press-contact-body" data-testid="press-kit-contact-body">
                {t('pressKit.contact.body')}
              </p>
              <a
                href="mailto:audyt@hussarai.org?subject=HussarAi%20Press%20Kit%20request"
                className="hussar-press-contact-email"
                data-testid="press-kit-contact-email"
              >
                audyt@hussarai.org
              </a>
            </div>
          </Card>
        </section>

        {/* Useful links */}
        <section data-testid="press-kit-links" style={{ marginTop: 44 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: CYAN }}>
            {t('pressKit.links.title')}
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid',
                       gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
            {linkItems.map((link, idx) => (
              <li key={idx}>
                <Link
                  to={link.to}
                  data-testid={`press-kit-link-${idx + 1}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: CYAN,
                    textDecoration: 'none',
                    padding: '8px 0',
                    fontSize: 15,
                  }}
                >
                  <ChevronRight className="w-4 h-4" />
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Disclaimer */}
        <section data-testid="press-kit-disclaimer-section" style={{ marginTop: 40 }}>
          <div
            data-testid="press-kit-disclaimer"
            style={{
              display: 'flex',
              gap: 10,
              padding: '12px 16px',
              background: 'rgba(15,23,42,0.5)',
              borderRadius: 10,
              color: 'rgba(148,163,184,0.9)',
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            <Info className="w-4 h-4 mt-0.5" style={{ flexShrink: 0 }} />
            <span>{t('pressKit.disclaimer')}</span>
          </div>
        </section>

        {/* v4.0 CTA row (doctrine-safe) */}
        <div className="hussar-agenci-cta-row" data-testid="press-kit-cta-row" style={{ marginTop: 44 }}>
          <Button
            size="lg"
            onClick={() => { track('press_kit_cta_review_demo', {}); navigate('/review-akt-imigracyjnych'); }}
            className="hussar-btn-primary hussar-btn-large"
            data-testid="press-kit-cta-review-demo"
          >
            {t('pressKit.ctas.primary')}
            <ChevronRight className="w-5 h-5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => { track('press_kit_cta_security', {}); navigate('/bezpieczenstwo-i-zgodnosc'); }}
            data-testid="press-kit-cta-security"
          >
            {t('pressKit.ctas.secondary')}
            <ArrowRight className="w-5 h-5" />
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => { track('press_kit_cta_back', {}); navigate('/'); }}
            data-testid="press-kit-cta-back"
          >
            {t('pressKit.ctas.back')}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PressKit;
